import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomTitle } from 'lib-base';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { firstValueFrom, forkJoin } from 'rxjs';
import {
  IGetDashboardTramiteActividadesPorDependencia,
  IGetDashboardTramitePorDependenciaDetalle,
  IGetTipoTramiteFuncionario,
  IGetTramiteRegistroSeleccionFechas
} from '../../../shared/interfaces/database/tramite';
import { TramRegistroService } from '../../../services/database/tramRegistro.service';
import { TranslateService } from '@ngx-translate/core';
import { FilterDialogComponent } from '../../../components/custom-work/other/filter-dialog/filter-dialog.component';
import { IGetTramEstado } from '../../../shared/interfaces/database/configuracion';
import { TramEstadoService } from '../../../services/database/tramEstado.service';
import { SecDependenciaService } from '../../../services/database/secDependencia.service';
import { DashboardTableComponent, ResumenEtapaConfig } from '../components/dashboard-table/dashboard-table.component';
import { ChartDonutCardConfig, ChartDonutComponent, ChartDonutSlice } from '../components/chart-donut/chart-donut.component';
import { DashboardTableSelectableComponent } from '../components/dashboard-table-selectable/dashboard-table-selectable.component';
import {HEX_COLOR_PALETTE} from "../components/utils/color-palette";

export interface ContentDetails {
  chart: ChartDonutCardConfig;
  dataChart: ChartDonutSlice[];
  funcionarioId: number;
  table: ResumenEtapaConfig;
}

@Component({
  selector: 'app-tramites-sisben',
  standalone: true,
  imports: [
    CommonModule,
    CustomTitle,
    ButtonModule,
    SkeletonModule,
    TableModule,
    FilterDialogComponent,
    DashboardTableComponent,
    DashboardTableSelectableComponent,
    ChartDonutComponent
  ],
  templateUrl: './tramites-sisben.component.html',
  styleUrl: './tramites-sisben.component.scss'
})
export class TramitesSisbenComponent implements OnInit {
  dependenciaId!: number;
  translations: any = {};

  size: number = 10;
  private readonly colors: string[] = HEX_COLOR_PALETTE;

  tramitePorDependencia: IGetDashboardTramitePorDependenciaDetalle[] = [];
  tramitePorDependenciaTotals!: IGetDashboardTramitePorDependenciaDetalle;
  isLoadingTramitePorDependencia: boolean = true;
  selectedTramitePorDependencia!: IGetDashboardTramitePorDependenciaDetalle;

  isDetailVisible: boolean = false;
  private tramiteActividadesPorDependencia: { [key: number]: IGetDashboardTramiteActividadesPorDependencia[] } = {};
  tramiteDetails: { [key: number]: ContentDetails } = {};
  isLoadingTramiteActividadesPorDependencia: boolean = true;

  delay = 300;
  private _tipoTramiteId!: number;

  filtrarMesText!: string;
  private _seleccionFechas: IGetTramiteRegistroSeleccionFechas[] = [];
  private _selectedSeleccionFecha!: IGetTramiteRegistroSeleccionFechas | undefined;
  @ViewChild('filtrarMes') filtrarMesDialogComponent!: FilterDialogComponent<IGetTramiteRegistroSeleccionFechas>;

  filtrarMesDetalleText!: string;
  private _selectedSeleccionFechaDetalle!: IGetTramiteRegistroSeleccionFechas | undefined;
  @ViewChild('filtrarMesDetalle') filtrarMesDetalleDialogComponent!: FilterDialogComponent<IGetTramiteRegistroSeleccionFechas>;

  filtrarEstadoTramiteText!: string;
  private _tramEstados: IGetTramEstado[] = [];
  private _selectedTramEstados!: IGetTramEstado | undefined;
  @ViewChild('filtrarEstadoTramite') filtrarEstadoTramiteDialogComponent!: FilterDialogComponent<IGetTramEstado>;

  filtrarFuncionarioDetalleText!: string;
  private _funcionarios: IGetTipoTramiteFuncionario[] = [];
  private _selectedFuncionariosDetalle!: IGetTipoTramiteFuncionario | undefined;
  @ViewChild('filtrarFuncionarioDetalle') filtrarFuncionarioDetalleDialogComponent!: FilterDialogComponent<IGetTipoTramiteFuncionario>;

  @ViewChildren(ChartDonutComponent) donutCharts!: QueryList<ChartDonutComponent>;
  defaultConfigTableDetails!: ResumenEtapaConfig;
  configTable!: ResumenEtapaConfig;

  constructor(
    private readonly _tr: TramRegistroService,
    private readonly _t: TranslateService,
    private readonly _sds: SecDependenciaService,
    private readonly _te: TramEstadoService
  ) {}

  //#region life cycle
  async ngOnInit(): Promise<void> {
    this.translate().then(() => {
      this.defaultConfigDetailsInit();
      this.configTableInit();
    });
    this.getTramEstados();
    await this.getDependenciaSisben();
    this.getDashboardTramitePorDependenciaDetalle();
    this.getTramiteRegistroSeleccionFechas();
  }
  //#endregion life cycle

  //#region init
  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      titulo1: this._t.get('components.app-detalle-tramite-dependencia.titulo1'),
      titulo2: this._t.get('components.app-detalle-tramite-dependencia.titulo2'),
      tramite: this._t.get('common.tramite'),
      recibidos: this._t.get('common.recibidos'),
      pendientes: this._t.get('common.pendientes'),
      finalizadosFueraPlazo: this._t.get('common.finalizados-fuera-plazo'),
      finalizadosATiempo: this._t.get('common.finalizados-a-tiempo'),
      completados: this._t.get('common.completados'),
      actividad: this._t.get('common.actividad'),
      actividadesAsignadas: this._t.get('msg.actividades-asignadas'),
      actividadesEjecutadasTiempoPlanificado: this._t.get('msg.actividades-ejecutadas-tiempo-planificado'),
      actividadesNoEjecutadasTiempoPlanificado: this._t.get('msg.actividades-no-ejecutadas-tiempo-planificado'),
      cumplimientos: this._t.get('common.cumplimientos'),
      incumplimientos: this._t.get('common.incumplimientos'),
      filtrarMes: this._t.get('msg.filtrar-mes'),
      filtrarEstadoTramite: this._t.get('msg.filtrar-estado-tramite'),
      mes: this._t.get('common.mes'),
      filtrarFuncionarioDetalle: this._t.get('msg.filtrar-funcionario'),
      mostrarDetalle: this._t.get('msg.mostrar-detalle'),
      estado: this._t.get('common.estado'),
      funcionario: this._t.get('common.funcionario'),
      totalGeneral: this._t.get('msg.total-general')
    }));

    this.filtrarMesText = this.translations.filtrarMes;
    this.filtrarMesDetalleText = this.translations.filtrarMes;
    this.filtrarEstadoTramiteText = this.translations.filtrarEstadoTramite;
    this.filtrarFuncionarioDetalleText = this.translations.filtrarFuncionarioDetalle;
  }

  private defaultConfigDetailsInit() {
    this.defaultConfigTableDetails = {
      data: [],
      columns: [
        {
          field: 'actividad',
          header: this.translations.actividad,
          type: 'text'
        },
        {
          field: 'actividadesAsignadas',
          header: this.translations.actividadesAsignadas,
          type: 'text'
        },
        {
          field: 'actividadesEjecutadasTiempoPlanificado',
          header: this.translations.actividadesEjecutadasTiempoPlanificado,
          type: 'text'
        },
        {
          field: 'actividadesNoEjecutadasTiempoPlanificado',
          header: this.translations.actividadesNoEjecutadasTiempoPlanificado,
          type: 'text'
        },
        {
          field: 'cumplimientos',
          header: this.translations.cumplimientos,
          type: 'progress',
          rowClass: 'cumplimientoColor',
          maxValue: 100,
          suffix: '%',
          cssClass: 'text-center'
        },
        {
          field: 'incumplimientos',
          header: this.translations.incumplimientos,
          type: 'progress',
          rowClass: 'incumplimientoColor',
          maxValue: 100,
          suffix: '%',
          cssClass: 'text-center'
        }
      ]
    };
  }

  private configTableInit() {
    this.configTable = {
      pageSize: this.size,
      columns: [
        {
          field: 'tipoTramiteDescripcion',
          header: this.translations.tramite,
          type: 'text',
          sortable: true
        },
        {
          field: 'recibidos',
          header: this.translations.recibidos,
          type: 'text',
          cssClass: 'text-center',
          sortable: true
        },
        {
          field: 'pendientes',
          header: this.translations.pendientes,
          type: 'text',
          cssClass: 'text-center',
          sortable: true
        },
        {
          field: 'finalizadosATiempo',
          header: this.translations.finalizadosATiempo,
          type: 'text',
          cssClass: 'text-center',
          sortable: true
        },
        {
          field: 'finalizadosFueraPlazo',
          header: this.translations.finalizadosFueraPlazo,
          type: 'text',
          cssClass: 'text-center',
          sortable: true
        },
        {
          field: 'porcentajePendientes',
          header: '% ' + this.translations.pendientes,
          type: 'progress',
          cssClass: 'text-center',
          rowClass: 'pendienteColor',
          maxValue: 100,
          suffix: '%',
          sortable: true
        },
        {
          field: 'porcentajeCompletado',
          header: '% ' + this.translations.completados,
          type: 'progress',
          maxValue: 100,
          suffix: '%',
          cssClass: 'text-center',
          rowClass: 'completadoColor',
          sortable: true
        }
      ],
      data: []
    };
  }

  private updateConfigTable() {
    this.configTable = {
      ...this.configTable,
      data: this.tramitePorDependencia?.map(item => ({
        ...item,
        porcentajePendientes: item.porcentajePendientes ? (item.porcentajePendientes * 100).toFixed(2) : 0,
        porcentajeCompletado: item.porcentajeCompletado ? (item.porcentajeCompletado * 100).toFixed(2) : 0,
        pendienteColor: 'gio-orange-bg',
        completadoColor: 'gio-green-bg'
      })),
      total: this.tramitePorDependenciaTotals as any
    };
  }
  //#endregion init

  //#region utils
  private getGroupedData(data: IGetDashboardTramiteActividadesPorDependencia[]) {
    return data.reduce((acc, curr) => {
      if (!acc[curr.funcionarioId]) {
        acc[curr.funcionarioId] = [];
      }
      acc[curr.funcionarioId].push(curr);
      return acc;
    }, {} as { [key: number]: IGetDashboardTramiteActividadesPorDependencia[] });
  }

  calcularPCompletados(recibidos: number, finalizados: number): number {
    if (recibidos === 0) {
      return 0;
    }
    return finalizados / recibidos;
  }

  calcularPPendiente(recibidos: number, pendientes: number): number {
    if (recibidos === 0) {
      return 0;
    }
    return pendientes / recibidos;
  }

  private calcTotals(data: IGetDashboardTramitePorDependenciaDetalle[]) {
    this.tramitePorDependenciaTotals = data.reduce((totals, item) => {
      totals.recibidos += item.recibidos;
      totals.pendientes += item.pendientes;
      totals.finalizadosATiempo += item.finalizadosATiempo;
      totals.finalizadosFueraPlazo += item.finalizadosFueraPlazo;
      return totals;
    }, {
      id: 0,
      tipoTramiteDescripcion: this.translations.totalGeneral,
      recibidos: 0,
      pendientes: 0,
      finalizadosATiempo: 0,
      finalizadosFueraPlazo: 0
    });

    const porcentajePendiente = (this.calcularPPendiente(
      this.tramitePorDependenciaTotals.recibidos,
      this.tramitePorDependenciaTotals.pendientes
    ) * 100)?.toFixed(2);

    this.tramitePorDependenciaTotals.porcentajePendientes = isNaN(Number(porcentajePendiente)) ? 0 : Number(porcentajePendiente);

    const finalizados = this.tramitePorDependenciaTotals.finalizadosATiempo + this.tramitePorDependenciaTotals.finalizadosFueraPlazo;

    const porcentajeCompletado = (this.calcularPCompletados(
      this.tramitePorDependenciaTotals.recibidos,
      finalizados
    ) * 100)?.toFixed(2);

    this.tramitePorDependenciaTotals.porcentajeCompletado = isNaN(Number(porcentajeCompletado)) ? 0 : Number(porcentajeCompletado);
  }

  private setTramiteDependencia(values: IGetDashboardTramitePorDependenciaDetalle[]) {
    this.tramitePorDependencia = values?.map(item => {
      const finalizados = item.finalizadosATiempo + item.finalizadosFueraPlazo;
      item.porcentajeCompletado = this.calcularPCompletados(item.recibidos, finalizados);
      item.porcentajePendientes = this.calcularPPendiente(item.recibidos, item.pendientes);
      return item;
    });
  }

  loadChartsData() {
    const arrow = () => {
      this.donutCharts?.forEach((component) => {
        const chartID = component.config.chartId;
        const id: number = Number(chartID.substring(chartID?.indexOf('-') + 1));
        if (isNaN(id) || !this.tramiteDetails[id]) return;
        const data = this.tramiteDetails[id].dataChart;
        component.load(data);
      });
    };
    setTimeout(arrow, this.delay);
  }

  private cleanDetails() {
    this.tramiteDetails = {};
  }
  //#endregion utils

  //#region listeners
  tramitePorDependenciaSelectionChange(event: IGetDashboardTramitePorDependenciaDetalle) {
    this._tipoTramiteId = event.id;
    this.getDashboardTramiteActividadesPorDependencia();
  }

  verDetalle(secUsuarioId: number) {
    const url = `/#/dashboard/detalle-actividades/${this._tipoTramiteId}/${this.dependenciaId}/${secUsuarioId}`;
    window.open(url, '_blank');
  }
  //#endregion listeners

  //#region Filter Dialogs
  openFiltrarMesDialog() {
    this.filtrarMesDialogComponent.setHeaderText(this.translations.filtrarMes);
    this.filtrarMesDialogComponent.InitializeDropdownFilter('monthYear', this._seleccionFechas);
    this.filtrarMesDialogComponent.showDialog();
  }

  filtrarMesOnSeleccionar(id: IGetTramiteRegistroSeleccionFechas) {
    this._selectedSeleccionFecha = id;
    this.filtrarMesText = id ? this.translations.mes + ': ' + id.monthYear : this.translations.filtrarMes;
    this.getDashboardTramitePorDependenciaDetalle();
  }

  openfiltrarEstadoTramiteDialog() {
    this.filtrarEstadoTramiteDialogComponent.setHeaderText(this.translations.filtrarEstadoTramite);
    this.filtrarEstadoTramiteDialogComponent.InitializeDropdownFilter('descripcion', this._tramEstados);
    this.filtrarEstadoTramiteDialogComponent.showDialog();
  }

  filtrarEstadoTramiteOnSeleccionar(id: IGetTramEstado) {
    this._selectedTramEstados = id;
    this.filtrarEstadoTramiteText = id ? this.translations.estado + ': ' + id.descripcion : this.translations.filtrarEstadoTramite;
    this.getDashboardTramitePorDependenciaDetalle();
  }

  openFiltrarMesDetalleDialog() {
    this.filtrarMesDetalleDialogComponent.setHeaderText(this.translations.filtrarMes);
    this.filtrarMesDetalleDialogComponent.InitializeDropdownFilter('monthYear', this._seleccionFechas);
    this.filtrarMesDetalleDialogComponent.showDialog();
  }

  filtrarMesDetalleOnSeleccionar(id: IGetTramiteRegistroSeleccionFechas) {
    this._selectedSeleccionFechaDetalle = id;
    this.filtrarMesDetalleText = id ? this.translations.mes + ': ' + id.monthYear : this.translations.filtrarMes;
    this.getDashboardTramiteActividadesPorDependencia();
  }

  openfiltrarFuncionarioDetalleDialog() {
    this.filtrarFuncionarioDetalleDialogComponent.setHeaderText(this.translations.filtrarFuncionarioDetalle);
    this.filtrarFuncionarioDetalleDialogComponent.InitializeDropdownFilter('funcionario', this._funcionarios);
    this.filtrarFuncionarioDetalleDialogComponent.showDialog();
  }

  filtrarFuncionarioDetalleOnSeleccionar(id: IGetTipoTramiteFuncionario) {
    this._selectedFuncionariosDetalle = id;
    this.filtrarFuncionarioDetalleText = id ? this.translations.funcionario + ': ' + id.funcionario : this.translations.filtrarFuncionarioDetalle;
    this.getDashboardTramiteActividadesPorDependencia();
  }
  //#endregion Filter Dialogs

  //#region build charts
  private buildChart(id: number, centerLabel: string): ChartDonutCardConfig {
    return {
      title: this.tramiteActividadesPorDependencia[id][0].funcionario,
      chart: {
        chartId: `chartDonut-${id}`,
        centerLabel: `${centerLabel}%`,
        centerSub: 'Cumplimiento',
        slices: []
      }
    };
  }

  private buildCards() {
    for (const id in this.tramiteActividadesPorDependencia) {
      if (!this.tramiteActividadesPorDependencia.hasOwnProperty(id)) return;

      const chartData: IGetDashboardTramiteActividadesPorDependencia[] = this.tramiteActividadesPorDependencia[id];
      const aggregatedData = chartData.reduce((acc, curr) => {
        acc.actividadesCompletadas += curr.actividadesCompletadas;
        acc.actividadesNoCompletadas += curr.actividadesNoCompletadas;
        return acc;
      }, { actividadesCompletadas: 0, actividadesNoCompletadas: 0 });

      const total = aggregatedData.actividadesCompletadas + aggregatedData.actividadesNoCompletadas;

      const aggregatedList: ChartDonutSlice[] = [
        {
          label: this.translations.cumplimientos,
          color: this.colors[0],
          value: total === 0 ? 0 : aggregatedData.actividadesCompletadas / total
        },
        {
          label: this.translations.incumplimientos,
          color: this.colors[2],
          value: total === 0 ? 0 : aggregatedData.actividadesNoCompletadas / total
        }
      ];
      const centerLabel = (aggregatedList[0].value * 100).toFixed(1) ?? '0';
      this.tramiteDetails[id] = {
        chart: this.buildChart(+id, centerLabel),
        funcionarioId: +id,
        dataChart: aggregatedList,
        table: {
          ...this.defaultConfigTableDetails,
          data: chartData.map(c => ({
            ...c,
            cumplimientos: c.cumplimientos != null ? (c.cumplimientos <= 1 ? +(c.cumplimientos * 100).toFixed(1) : c.cumplimientos) : 0,
            incumplimientos: c.incumplimientos != null ? (c.incumplimientos <= 1 ? +(c.incumplimientos * 100).toFixed(1) : c.incumplimientos) : 0,
            cumplimientoColor: 'gio-green-bg',
            incumplimientoColor: 'gio-orange-bg'
          }))
        }
      };
    }
    this.loadChartsData();
  }
  //#endregion build charts

  //#region API calls
  getDashboardTramitePorDependenciaDetalle() {
    if (!this.dependenciaId) return;
    this.isLoadingTramitePorDependencia = true;
    this.isDetailVisible = false;
    this._tr.getDashboardTramitePorDependenciaDetalle(
      this.dependenciaId,
      this._selectedTramEstados?.id,
      this._selectedSeleccionFecha?.firstDayOfMonth
    ).subscribe({
      next: (data: IGetDashboardTramitePorDependenciaDetalle[]) => {
        this.setTramiteDependencia(data);
        this.calcTotals(data);
        this.updateConfigTable();
      },
      complete: () => {
        this.isLoadingTramitePorDependencia = false;
      }
    });
  }

  getDashboardTramiteActividadesPorDependencia() {
    this.isDetailVisible = true;
    this.isLoadingTramiteActividadesPorDependencia = true;
    this.tramiteActividadesPorDependencia = {};
    this.cleanDetails();
    this._tr.getDashboardTramiteActividadesPorDependencia(
      this._tipoTramiteId,
      this.dependenciaId,
      this._selectedFuncionariosDetalle?.funcionarioId,
      this._selectedSeleccionFechaDetalle?.firstDayOfMonth
    ).subscribe({
      next: (data: IGetDashboardTramiteActividadesPorDependencia[]) => {
        this.tramiteActividadesPorDependencia = this.getGroupedData(data);
        this.buildCards();
        this.getTipoTramiteFuncionario();
      },
      complete: () => {
        this.isLoadingTramiteActividadesPorDependencia = false;
      }
    });
  }

  getTramiteRegistroSeleccionFechas() {
    this._tr.getTramiteRegistroSeleccionFechas().subscribe({
      next: (data: IGetTramiteRegistroSeleccionFechas[]) => {
        this._seleccionFechas = data?.sort((a, b) => (a.firstDayOfMonth > b.firstDayOfMonth ? -1 : 1));
      }
    });
  }

  getTramEstados() {
    this._te.getTramEstados().subscribe({
      next: (data: IGetTramEstado[]) => {
        this._tramEstados = data;
      }
    });
  }

  getTipoTramiteFuncionario() {
    this._tr.getTipoTramiteFuncionario(this._tipoTramiteId).subscribe({
      next: (data: IGetTipoTramiteFuncionario[]) => {
        this._funcionarios = data;
      }
    });
  }

  private async getDependenciaSisben(): Promise<number> {
    const value = await firstValueFrom(this._sds.getIdDependenciaSisben());
    this.dependenciaId = value;
    return value;
  }
  //#endregion API calls
}
